import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import { DebtorForm } from "@/components/debtors/DebtorForm";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { TIERS } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const TIER_KEYS = new Set(["bronze", "silver", "gold"]);

/**
 * Full-form edit modal used by the debtor profile header's "Edit Profile" button.
 * Pre-populates every DebtorForm field with the current debtor data and PATCHes
 * to /debtors/:id on save.
 */
const EditDebtorProfileModal = ({ open, debtor, onClose, onSaved }) => {
  const [draft, setDraft] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && debtor) {
      setDraft({
        full_name: debtor.full_name || "",
        aliases: debtor.aliases || [],
        dob: debtor.dob || "",
        ssn_last4: debtor.ssn_last4 || "",
        current_address: debtor.current_address || {},
        prior_addresses: debtor.prior_addresses || [],
        current_employer: debtor.current_employer || { address: {} },
        prior_employers: debtor.prior_employers || [],
        employment_status: debtor.employment_status || "unknown",
        assets: debtor.assets || {},
        judgment: debtor.judgment || {},
        documents: debtor.documents || [],
        tier: TIER_KEYS.has(debtor.tier) ? debtor.tier : "bronze",
      });
    }
  }, [open, debtor]);

  const patch = (partial) => setDraft((d) => ({ ...d, ...partial }));

  const submit = async () => {
    if (!draft) return;
    if (!(draft.full_name || "").trim()) {
      toast.error("Debtor name is required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: draft.full_name,
        aliases: draft.aliases,
        dob: draft.dob || null,
        ssn_last4: draft.ssn_last4 || null,
        current_address: draft.current_address,
        prior_addresses: draft.prior_addresses,
        current_employer: draft.current_employer,
        prior_employers: draft.prior_employers,
        employment_status: draft.employment_status,
        assets: draft.assets,
        judgment: draft.judgment,
        documents: draft.documents,
        tier: draft.tier,
      };
      await apiClient.patch(`/debtors/${debtor.id}`, payload);
      toast.success("Debtor profile updated.");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && draft && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={submitting ? undefined : onClose}
          />
          <motion.div
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            data-testid="edit-debtor-modal"
          >
            <div
              className="w-full max-w-[900px] max-h-[92vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 pb-4 flex items-start justify-between">
                <div>
                  <div className="jea-caption">Edit Profile</div>
                  <h2 className="text-white font-bold text-[22px] mt-1">
                    {debtor?.full_name || "Debtor"}
                  </h2>
                  <p className="text-text-secondary text-[13px] mt-1">
                    Update any section. Tier changes recompute the next scheduled query.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="p-2 text-text-secondary hover:text-white disabled:opacity-40"
                  data-testid="edit-debtor-close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="px-8 pb-8">
                <DebtorForm debtor={draft} patch={patch} subIndex={null} />

                {/* Tier picker */}
                <div className="mt-8">
                  <div className="jea-caption mb-2">Tier</div>
                  <div className="grid grid-cols-3 gap-3" data-testid="edit-debtor-tier-picker">
                    {TIERS.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => patch({ tier: t.key })}
                        className={cn(
                          "flex flex-col items-center gap-1 py-3 rounded-[8px] border transition-all",
                          draft.tier === t.key
                            ? "bg-bg-primary border-accent-pink shadow-pink-glow"
                            : "bg-bg-primary border-border-default hover:border-accent-pink/60"
                        )}
                        data-testid={`edit-debtor-tier-${t.key}`}
                      >
                        <Shield size={20} style={{ color: t.color }} />
                        <span className="text-[11px] font-bold tracking-[0.1em]" style={{ color: t.color }}>
                          {t.label}
                        </span>
                        <span className="text-[11px] text-text-secondary">{t.frequency}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 px-8 py-4 bg-bg-secondary border-t border-border-default flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors disabled:opacity-40"
                  data-testid="edit-debtor-cancel"
                >
                  Cancel
                </button>
                <JeaButton onClick={submit} loading={submitting} data-testid="edit-debtor-save">
                  Save Changes
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditDebtorProfileModal;
